import {ResourceRef, Signal} from '@angular/core';
import {IntervalWorker} from './interval-worker';

export type Primitive = number | string | boolean | Date;
export type NullablePrimitive = Primitive | null | undefined;

export class Stdlib {

    static MAX_DATE = new Date(Number.MAX_VALUE);
    static MIN_DATE = new Date(0);

    /**
     * Creates a deep clone of the provided value. This method duplicates the entire structure of the input,
     * preserving the data while ensuring no shared references to the input object.
     *
     * @param {any} value - The value to be cloned. It can be of any type, including primitives, objects, and arrays.
     * @return {any} Returns a new deep copy of the input value. If the input is undefined or null, the method directly returns the input.
     */
    static clone<T>(value: T): T {
        if (value === undefined || value === null) {
            return value;
        } else {
            return JSON.parse(JSON.stringify(value));
        }
    }

    /**
     * This is Kotlin's `let` function.
     * Executes the given function with the value if it is not null or undefined and returns the result.
     * @param value The value to check.
     * @param code A function to apply to the value if it is not null or undefined.
     */
    static let<T, V>(value: T | undefined | null, code: (it: T) => V): V | undefined {
        return value == null ? undefined : code(value);
    }

    static let2<T1, T2, V>(value1: T1 | undefined | null, value2: T2 | undefined | null, code: (v1: T1, v2: T2) => V): V | undefined {
        return value1 == null || value2 == null ? undefined : code(value1, value2);
    }

    /**
     * Converts the input value to a Date object. When the value is already a Date object, it is returned as is.
     * Otherwise, it attempts to create a new Date object from the input value.
     *
     * @param {any} value - The input value to be converted.
     * @return {Date | undefined} A Date object if the conversion is successful, or undefined if the input value is null or undefined.
     */
    static toDate(value: any): Date | undefined {
        if (value == null) {
            return undefined;
        } else if (value instanceof Date) {
            return value;
        } else {
            return new Date(value);
        }
    }

    static maxDate(a: Date | undefined | null, b: Date | undefined | null): Date | undefined {
        if (a == null || b == null) {
            return a ?? b ?? undefined;
        } else {
            return a > b ? a : b;
        }
    }

    static maxOfDates(dates: Date[]): Date | undefined {
        if (dates.length === 0) {
            return undefined;
        } else {
            return dates.reduce((a, b) => a > b ? a : b);
        }
    }

    static deleteAllUndefinedOrNull(obj: any): any {
        for (const key in obj) {
            if (obj[key] === undefined || obj[key] === null) {
                delete obj[key];
            }
        }
        return obj;
    }

    /**
     * Debounces a function call by waiting for a specified number of milliseconds.
     *
     * https://stackoverflow.com/questions/79321123/debouncing-a-search-term-when-using-the-new-angular-resource-api
     * @param ms     The number of milliseconds to wait before calling the function.
     * @param signal An AbortSignal to cancel the operation if needed.
     */
    static debounceWaitWithAbortSignal(ms: number, signal: AbortSignal) {
        return new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => resolve(), ms);

            signal.addEventListener('abort', () => {
                clearTimeout(timeout);
                reject(new Error('Operation aborted'));
            }, { once: true });
        });
    }

    static signalToNonNullPromise<T>(signal: Signal<T>, timeout: number = 60_000): Promise<T> {
        const value = signal();
        if (value != null) {
            return Promise.resolve(value);
        } else {
            const t0 = Date.now();
            return new Promise<T>((resolve, reject) => {
                const w = new IntervalWorker('waitForSignalToHaveData', 50, () => {
                    try {
                        const value = signal();
                        if (value != null) {
                            w.stop();
                            resolve(value);
                        } else {
                            if (Date.now() - t0 > timeout) {
                                throw new Error('Signal did not have data within the specified timeout.');
                            }
                        }
                    } catch (e) {
                        console.error('Error during waitForResourceToHaveData:', e);
                        w.stop();
                        reject(e);
                    }
                }, true);
            });
        }
    }

    static resourceRefId = 0;

    /**
     * Waits for a resource to have data, polling every 50ms until the timeout is reached.
     * If the resource already has data, it resolves immediately.
     *
     * @param resource The resource to check for data.
     * @param timeout The maximum time to wait for the resource to have data, in milliseconds (default: 60 seconds).
     * @returns A promise that resolves with the resource's value when it has data, or rejects if the timeout is reached.
     */
    static resourceDataToPromise<T>(resource: ResourceRef<T | undefined>, timeout: number = 60_000): Promise<T> {
        const refId = Stdlib.resourceRefId++;
        console.log(`[${refId}] Waiting for resource to have data...`);
        if (resource.hasValue()) {
            return Promise.resolve(resource.value()!);
        } else {
            const t0 = Date.now();
            return new Promise<T>((resolve, reject) => {
                const w = new IntervalWorker('waitForResourceToHaveData', 50, () => {
                    try {
                        if (resource.hasValue()) {
                            w.stop();
                            resolve(resource.value()!);
                        } else {
                            if (Date.now() - t0 > timeout) {
                                throw new Error('Resource did not have data within the specified timeout.');
                            }
                        }
                    } catch (e) {
                        console.error(`[${refId}] Error during waitForResourceToHaveData:`, e);
                        w.stop();
                        reject(e);
                    }
                }, true);
            });
        }
    }

    static signalPredicateToPromise<T>(signal: Signal<T>, predicate: (value: T) => boolean, timeout: number = 60_000): Promise<T> {
        const value = signal();
        if (value != null && predicate(value)) {
            return Promise.resolve(value);
        } else {
            const t0 = Date.now();
            return new Promise<T>((resolve, reject) => {
                const w = new IntervalWorker('waitForSignalToHaveData', 50, () => {
                    try {
                        const value = signal();
                        if (value != null && predicate(value)) {
                            w.stop();
                            resolve(value);
                        } else {
                            if (Date.now() - t0 > timeout) {
                                throw new Error('Signal did not have data within the specified timeout.');
                            }
                        }
                    }
                    catch (e) {
                        console.error('Error during waitForSignalToHaveData:', e);
                        w.stop();
                        reject(e);
                    }
                })
            });
        }
    }

    /**
     * Waits for a signal to have data, polling every 50ms until the timeout is reached.
     * If the signal already has data, it resolves immediately.
     *
     * @param predicate - The predicate function that determines if the signal has data.
     * @param timeout   - The maximum time to wait for the signal to have data, in milliseconds (default: 60 seconds).
     */
    static waitUntil(predicate: () => boolean, timeout: number = 10_000): Promise<void> {
        if (predicate()) {
            return Promise.resolve();
        } else {
            const t0 = Date.now();
            return new Promise<void>((resolve, reject) => {
                const w = new IntervalWorker('waitForSignalToHaveData', 50, () => {
                    try {
                        if (predicate()) {
                            w.stop();
                            resolve();
                        } else if (Date.now() - t0 > timeout) {
                            w.stop();
                            reject(new Error('Timeout waiting for predicate to be true.'));
                        }
                    } catch (e) {
                        console.error('Error during waitForSignalToHaveData:', e);
                        w.stop();
                        reject(e);
                    }
                });
            });
        }
    }

    static difference(s1: Set<string>, s2: Set<string>): Set<string> {
        const difference = new Set<string>();
        for (const id of s1) {
            if (!s2.has(id)) {
                difference.add(id);
            }
        }
        return difference;
    }

    /**
     * Lets you determine the value returned by a query based on whether a specified expression is null or not null.
     * If expr1 is not null, then NVL2 returns expr2. If expr1 is null, then NVL2 returns expr3.
     */
    static nvl2<A, B, C>(expr1: A, expr2: B, expr3: C): B | C {
        if (expr1 != null) return expr2;
        else return expr3;
    }

    static touch(signals: Signal<any>[]) {
        for (const signal of signals) {
            signal();
        }
    }

    /**
     * Invokes each signal function in the provided array of signals. This is useful for triggering any side effects
     * or updates that are associated with the signals, even if their values are not being directly used.
     * By calling each signal, you can ensure that any reactive computations or subscriptions
     * that depend on these signals are executed, allowing for proper synchronization
     * of state and UI updates in an Angular application.
     */
    static touchSignals(...signals: Signal<any>[]) {
        for (const signal of signals) {
            signal();
        }
    }

    /**
     * Pauses the execution of code for the specified number of milliseconds.
     *
     * @param {number} ms - The number of milliseconds to pause execution.
     * @return {Promise<void>} A promise that resolves after the specified duration.
     */
    static sleep(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    static isNumberOrNull(value: unknown): value is number | null {
        return value == null || typeof value === 'number';
    }
}
